import React from 'react'
import getChannelType from 'fixtures/channelTypes'
import { Text } from '.'

const DEFAULT_MAX_ROWS = 3

/**
 * The text of an open-conversation row: "inbox name / contact name". The inbox is the conversation's queue (the channel's
 * own name when there's no queue); the contact is the first non-agent party — its contact's full name, else the party's own
 * name or address (a contactless party). Either half is left out when it's unknown.
 */
export const getOpenConversationLabel = (conversation) => {
  const channel = conversation?.channel
  const inboxName = conversation?.queue?.name || channel?.name || ''
  const party = (conversation?.parties || []).find(p => (p?.contact || p?.name || p?.address) && !p?.agent)
  const contactName = `${party?.contact?.first_name || ''} ${party?.contact?.last_name || ''}`.trim() || party?.name || party?.address || ''
  return [inboxName, contactName].filter(Boolean).join(' / ')
}

/**
 * The record's open conversations as the rows the Lead list card shows: the channel's icon, "inbox name / contact name"
 * (see getOpenConversationLabel) and a red dot while a reply is pending (pending_reply_at set). Capped at `max` (default 3), with a "+N more conversation(s)" line, so a
 * record with many threads can't blow the card up. The issue / lead list endpoints carry `open_conversations`; the result
 * is [] when there are none. `selected` flips to the tints a colored (selected) card needs.
 */
export const getOpenConversationRows = (item, { selected = false, max = DEFAULT_MAX_ROWS } = {}) => {
  const conversations = Array.isArray(item?.open_conversations) ? item.open_conversations : []
  const shown = conversations.slice(0, max)
  const hiddenCount = conversations.length - shown.length
  const textColor = selected ? 'textSecondaryContrast' : 'textSecondary'

  const rows = shown.map((conversation, i) => {
    const channel = conversation?.channel
    const image = channel
      ? getChannelType(channel.account_type ? channel.account_type.code : channel.channel_type, channel.name || null).image
      : null
    return (
      <div key={`open-conversation-${conversation?.uid ?? i}`} style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
        {image && <img src={image} alt={channel?.name || ''} style={{ width: 18, maxWidth: 18, flexShrink: 0 }} />}
        <Text variant="body2" component="span" noWrap={true} color={textColor} textCustomProps={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {getOpenConversationLabel(conversation)}
        </Text>
        {conversation?.pending_reply_at &&
          <span
            title="Waiting for your reply"
            style={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0, marginLeft: 'auto', marginRight: 2, backgroundColor: selected ? '#ff1744' : '#d32f2f', boxShadow: selected ? '0 0 0 1px #fff' : undefined }}
          />
        }
      </div>
    )
  })

  if (hiddenCount > 0) {
    rows.push(
      <div key="open-conversations-more">
        <Text variant="body2" component="span" color={textColor}>
          +{hiddenCount} more conversation{hiddenCount === 1 ? '' : 's'}
        </Text>
      </div>
    )
  }
  return rows
}

/**
 * Scheme component `{ "component": "OpenConversations", "max": 3 }` — a board card field, or a table column's
 * primary / secondary / third. Renders nothing when there are no open conversations. `compact` drops the top offset a
 * card row needs (a table cell sits flush).
 */
const OpenConversationRows = ({ item, max, selected, compact = false }) => {
  const rows = getOpenConversationRows(item, { max, selected })
  if (rows.length === 0) return null
  return <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4, marginTop: compact ? 0 : 6 }}>{rows}</div>
}

export default OpenConversationRows
